/**
 * Parse a string from a `<TextField type="number">` for a signed monetary
 * correction (USD, positive credits the partner, negative debits). Returns a
 * discriminated union so callers can distinguish a valid signed value from
 * the specific failure mode — `parseFloat` would silently turn `''` and
 * `'abc'` into `NaN`, which the BE would reject in a confusing way.
 *
 * Matches the BE constraint on `AffiliateAdminCommissionCorrectionSchema`:
 * non-zero, fits 9 digits with 2 decimals. Empty input is always rejected
 * (the BE requires a non-zero amount).
 */
export type ParseSignedAmountReason =
  | 'empty'
  | 'not-finite'
  | 'zero'
  | 'too-many-decimals'
  | 'overflow'

export type ParseSignedAmountResult =
  | { ok: true; value: number }
  | { ok: false; reason: ParseSignedAmountReason }

const MAX_INTEGER_DIGITS = 9 // matches the BE Decimal(max_digits=11, decimal_places=2)
const MAX_DECIMALS = 2

export const parseSignedAmount = (raw: string): ParseSignedAmountResult => {
  const trimmed = raw.trim()

  if (trimmed === '') {
    return { ok: false, reason: 'empty' }
  }

  // Reject any non-numeric character before Number() lossy-parses (e.g.
  // `Number('1e2') → 100` would dodge the digit check below). The optional
  // leading '+' or '-' plus digits with optional decimals is the full grammar.
  if (!/^[+-]?\d+(\.\d+)?$/.test(trimmed)) {
    return { ok: false, reason: 'not-finite' }
  }

  const n = Number(trimmed)

  if (!Number.isFinite(n)) {
    return { ok: false, reason: 'not-finite' }
  }
  if (n === 0) {
    return { ok: false, reason: 'zero' }
  }

  // Cap precision: more than 2 decimals would round inconsistently between
  // JS Number → BE Decimal. Reject explicitly so the operator notices.
  const decimalPart = trimmed.split('.')[1]
  if (decimalPart !== undefined && decimalPart.length > MAX_DECIMALS) {
    return { ok: false, reason: 'too-many-decimals' }
  }

  // 9 integer digits cap (max ±999_999_999.99). The BE Decimal rejects beyond
  // that with a 422; surface it client-side as a friendlier message.
  const integerPart = trimmed.replace(/^[+-]/, '').split('.')[0]
  if (integerPart !== undefined && integerPart.replace(/^0+/, '').length > MAX_INTEGER_DIGITS) {
    return { ok: false, reason: 'overflow' }
  }

  return { ok: true, value: n }
}
